/**
 * Product Review Component
 *
 * Displays product rating and review count using the Rating component.
 * Fetches actual review statistics from the reviews API.
 */

'use client';

import { Rating, ComponentSize } from '@template-project/afrodita-components';
import { useTranslation } from '@/lib/i18n/client';
import { useReviewStats } from '@/lib/hooks/use-reviews';

export interface ProductReviewProps {
  readonly productId: string;
  readonly fallbackRating?: number;
  readonly fallbackCount?: number;
}

export function ProductReview({ productId, fallbackRating = 0, fallbackCount = 0 }: ProductReviewProps) {
  const { t } = useTranslation('pdp');
  const hasFallbackStats = fallbackCount > 0;
  const shouldFetchStats = Boolean(productId && !hasFallbackStats);
  const { data: stats, isLoading } = useReviewStats(productId, shouldFetchStats);

  const hasStats = shouldFetchStats && stats !== undefined;
  const displayRating = hasStats
    ? (stats.averageRating ?? 0)
    : fallbackRating;
  const displayCount = hasStats
    ? stats.totalReviews
    : fallbackCount;

  if (isLoading) {
    return null;
  }

  if (displayCount === 0) {
    return null;
  }

  return (
    <Rating
      rating={displayRating}
      reviews={displayCount}
      reviewsText={displayCount === 1 ? t('reviews.reviewText') : t('reviews.reviewTextPlural')}
      size={ComponentSize.Small}
      showRatingText
      wrapWithParenthesis
      underline
      reviewsContainerId="#reviews-section"
    />
  );
}
